import { useState } from 'react';
import { Button, Card, Descriptions, Space, Typography } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import type { Fleet } from '@goelectric/shared-types';
import { BalanceAdjustModal } from '@/features/clientes/components/BalanceAdjustModal';
import { formatCop } from '../utils/format';

export interface FleetAccountCardProps {
  fleet: Fleet;
  /** Sin permiso de escritura no se ofrece el ajuste de saldo. */
  canWrite: boolean;
  /** Refresca la flota tras un ajuste exitoso (el saldo es derivado del ledger). */
  onAdjusted: () => void;
}

/**
 * Cuenta Maestra de la flota (US-2.3, ADR 0047).
 *
 * El saldo es el del **mismo ledger** `cliente_movimientos` del cliente FLOTA; no hay
 * ledger paralelo. Se reutiliza `BalanceAdjustModal` de Clientes, que llama al endpoint
 * dedicado `POST /api/v1/admin/customers/{masterAccountId}/balance-adjustments` con
 * `Idempotency-Key`, en lugar de duplicar el formulario de ajuste.
 */
export function FleetAccountCard({ fleet, canWrite, onAdjusted }: FleetAccountCardProps) {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const account = fleet.account;

  return (
    <Card
      title="Cuenta Maestra"
      extra={
        canWrite ? (
          <Button icon={<DollarOutlined />} onClick={() => setAdjustOpen(true)}>
            Ajustar saldo
          </Button>
        ) : undefined
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary">Saldo disponible</Typography.Text>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {formatCop(account?.balanceCop)} {account?.currency ?? 'COP'}
          </Typography.Title>
        </div>

        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Cuenta Maestra">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{fleet.masterAccountId}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Facturación">Unificada</Descriptions.Item>
          <Descriptions.Item label="Conductores">{fleet.driverCount}</Descriptions.Item>
          <Descriptions.Item label="Vehículos">{fleet.vehicleCount}</Descriptions.Item>
        </Descriptions>

        <Typography.Text type="secondary">
          Los movimientos de saldo quedan auditados con motivo y autor. El saldo es
          derivado del ledger de la Cuenta Maestra.
        </Typography.Text>
      </Space>

      <BalanceAdjustModal
        open={adjustOpen}
        customerId={fleet.masterAccountId}
        currentBalanceCop={account?.balanceCop ?? 0}
        onClose={() => setAdjustOpen(false)}
        onAdjusted={onAdjusted}
      />
    </Card>
  );
}
